import { EErrorCode } from 'config/errors-codes';
import { errorCollection } from 'config/ui/error-collection';
import { IPublicSession, userSession } from 'state/session';
import { showToast } from 'state/toast';
import { user } from 'utils/auth/user';
import { getImages } from 'utils/network/getCarImages';
import { tenantConfig } from 'utils/tenant';
import { i18n } from '@lingui/core';
import { State } from 'utils/state';
import logger from 'utils/log';
import { isObject } from 'utils/objects';
import { ICarImageElement, IVehicle } from 'data/schema';
import { updateVehicle } from './vehicleRegistry';
import { generateThumbnailUrl } from 'utils/network/preload';
import { selectImage } from 'utils/sync/imageSelection';

/**
 * Backwards compatibility bridge for deprecated currentImage field
 * Warns users and redirects to new selectImage() API
 */
export function initCurrentImageBridge(state: State<IPublicSession>) {
  let hasWarned = false;

  state.on('change', (changedKeys, changedValues) => {
    // `changedValues` is a positional array parallel to `changedKeys` (see State.setValues),
    // NOT a keyed object — read the new value by index. The previous `changedValues.currentImage`
    // was always `undefined`, so this bridge silently never fired.
    const index = changedKeys.indexOf('currentImage');
    const newValue = index === -1 ? undefined : changedValues[index];

    // Only bridge a real numeric image index. `changedValues` is loosely typed and this is a
    // deprecated integrator-facing field, so guard against a non-number (null/garbage) rather
    // than casting it straight through to selectImage().
    if (typeof newValue === 'number') {
      // Only warn once per session to avoid console spam
      if (!hasWarned) {
        logger.warn(
          '[DEPRECATED] session.currentImage is deprecated and will be removed in a future version. ' +
            'Use runtime.selectImage(index) instead. ' +
            'See documentation: https://docs.example.com/image-selection'
        );
        hasWarned = true;
      }

      // Bridge to new system for backwards compatibility
      selectImage(newValue);
    }
  });
}

export function initCarImagesAutoUpdate(state: State<IPublicSession>) {
  state.on('change', (changedKeys, changedValues, oldValues, newValues, session) => {
    // Skip auto-update if integrator has taken control of images
    if (session.imageOverrideMode === 'manual') {
      return;
    }

    // One of these keys has to change in order to resume the auto-update
    const significantChanges = ['prString', 'activeExperience', 'id', 'imageOverrideMode', 'comparisonMode'].some(
      (key) => changedKeys.includes(key)
    );

    // Comparison mode requires 2D images even while in 3D experience
    const inComparisonMode = session.comparisonMode === 'selecting' || session.comparisonMode === 'on';

    // The session must meet these requirements to fetch images
    const minimalDataPresent =
      user.type === 'dealer' &&
      session.id &&
      (session.activeExperience === '2D' || inComparisonMode) &&
      session.prString;

    // AVS-4161: When carImages is externally cleared (e.g., 3D stream's onUpdatePrString
    // fires with the same prString), none of the "significant" keys change. Detect this
    // specific case — carImages went from defined to undefined — and re-fetch.
    const imagesClearedExternally =
      changedKeys.includes('carImages') && session.carImages === undefined && !significantChanges;

    if (
      (significantChanges || imagesClearedExternally) &&
      !!minimalDataPresent &&
      session.carImages?.prString !== session.prString // Verify if the prString has changed
    ) {
      fetchImages();
    }
  });
}

/**
 * Fetches images and returns a Promise that resolves when loading completes.
 * Used for comparison validation where we need to wait for images before proceeding.
 * @returns Promise that resolves to true if images loaded successfully, false otherwise
 */
export async function fetchImagesAndWait(): Promise<boolean> {
  if (!is2DFeatureAllowed()) {
    return false;
  }

  const session = userSession().get();

  if (!session.prString && !session.vinOrMarketingCode) {
    return false;
  }

  userSession().setValues({ carImagesStatus: 'loading' });

  try {
    const images = await getImages({
      prString: session.prString,
      brand: session.brand!,
      market: session.market!,
      vinOrMarketingCode: session.vinOrMarketingCode,
    });

    // make sure it's not a stale request from another car
    if (images.prString === session.prString || images.okapiCode === session.vinOrMarketingCode) {
      const imagesShown = showImages({ images: images.items, index: 0 });
      if (!imagesShown) {
        userSession().setValues({ carImagesStatus: 'unset' });
        return false;
      }
      return true;
    }
    return false;
  } catch (error) {
    logger.error('Error fetching 2D content:', error);
    userSession().setValues({ carImagesStatus: 'unset' });
    showToast({
      type: 'error',
      title: i18n._(errorCollection[EErrorCode.IMAGES_NOT_AVAILABLE].title()),
      message: i18n._(errorCollection[EErrorCode.IMAGES_NOT_AVAILABLE].message()),
      notificationId: errorCollection[EErrorCode.IMAGES_NOT_AVAILABLE].notificationId,
    });
    return false;
  }
}

export function fetchImages() {
  if (!is2DFeatureAllowed()) {
    return;
  }

  const session = userSession().get();

  if (!session.prString && !session.vinOrMarketingCode) {
    return;
  }

  // don't show the loading state if something is already visible to the user
  if (session.carImagesStatus === 'unset') {
    userSession().setValues({ carImagesStatus: 'loading' });
  }

  getImages({
    prString: session.prString,
    brand: session.brand!,
    market: session.market!,
    vinOrMarketingCode: session.vinOrMarketingCode,
  })
    .then((images) => {
      // make sure it's not a stale request from another car
      if (images.prString === session.prString || images.okapiCode === session.vinOrMarketingCode) {
        const imagesShown = showImages({ images: images.items, index: 0 });
        if (!imagesShown) {
          userSession().setValues({ carImagesStatus: 'unset' });
        }
      }
    })
    .catch((error) => {
      logger.error('Error fetching 2D content:', error);
      userSession().setValues({ carImagesStatus: 'unset' });
      showToast({
        type: 'error',
        title: i18n._(errorCollection[EErrorCode.IMAGES_NOT_AVAILABLE].title()),
        message: i18n._(errorCollection[EErrorCode.IMAGES_NOT_AVAILABLE].message()),
        notificationId: errorCollection[EErrorCode.IMAGES_NOT_AVAILABLE].notificationId,
      });
    });
}

export function showImages({
  images = [],
  index = 0,
  override = false,
}: { images?: ICarImageElement[]; index?: number; override?: boolean } = {}) {
  if (!is2DFeatureAllowed()) {
    return;
  }

  const session = userSession().get();

  // Prevent carImages updates when in 3D mode - images are only relevant for 2D experience
  // This prevents race conditions where carImages sync can overwrite aveSessionId during session startup
  // EXCEPTION: Allow images in 3D mode when comparison mode is active (selecting or on),
  // or when an integrator explicitly overrides via the runtime API.
  if (session.activeExperience === '3D' && session.comparisonMode === 'off' && !override) {
    logger.info('Skipping carImages update: in 3D mode with comparison off where images are not used');
    return;
  }

  // If no images are provided, we fetch them
  // this may be an error from the runtime call or attempt to remove temp images
  if (images === null) {
    fetchImages();
    return;
  }

  if (!session.prString) {
    logger.error('No prString available to show car images');
    return;
  }

  if (index < 0 || index >= images.length) {
    logger.warn(`Provided image index ${index} is out of bounds (0-${images.length - 1}). Resetting to 0.`);
    index = 0;
  }

  if (validateImages(images)) {
    // AVS-4270: a blocked (outdated) car will never get images — mark the gallery
    // 'ready' so Stream/View renders the full-screen "Images are not available"
    // panel, and skip the toast that panel supersedes (PO decision 3)
    const emptyForBlockedCar = !images.length && userSession().is3DBlocked;
    userSession().setValues(
      {
        // Set override mode to 'manual' if integrator provided images
        imageOverrideMode: override ? 'manual' : session.imageOverrideMode,
      },
      { emit: false }
    );
    updateVehicle({
      carImages: {
        prString: session.prString,
        items: images,
      },
      // when there are no images, show the default background (blocked cars excepted — see above)
      carImagesStatus: images.length || emptyForBlockedCar ? 'ready' : 'unset',
    });

    // Select the specified image index (will sync via registry if Micro2DApp is mounted)
    if (images.length > 0) {
      selectImage(index);
    }

    if (!images.length && !emptyForBlockedCar) {
      showToast({
        type: 'info',
        title: i18n._(errorCollection[EErrorCode.IMAGES_NOT_AVAILABLE].title()),
        message: i18n._(errorCollection[EErrorCode.IMAGES_NOT_AVAILABLE].message()),
        notificationId: errorCollection[EErrorCode.IMAGES_NOT_AVAILABLE].notificationId,
      });
    }
    return true;
  }
}

// If we used custom images, we may want to reset and go back to the fetched images
// this will reset the index too. In the future we may want to create a smarter reset procedure
export function resetImages() {
  fetchImages();
}

/**
 * Allows integrators to give image control back to 3D Exhibition.
 * This resets the override mode to 'auto' and fetches images from our APIs.
 * Useful when integrators want to stop providing custom images and let 3DE take over.
 */
export function resetImageOverride() {
  userSession().setValues({ imageOverrideMode: 'auto' });
  fetchImages();
}

// verify if 2D is (still) allowed
function is2DFeatureAllowed() {
  if (!tenantConfig().isAllowed('2D')) {
    if (tenantConfig().isLoaded && !userSession().sessionEnded) {
      // if 2D feature is disabled, we will load 3D instead
      userSession().setValues({ activeExperience: '3D' });
    }

    return false;
  }
  return true;
}

/**
 * Validates the structure of car images.
 * Each image object is expected to have at least a 'source' property of type string.
 * @param images - Array of car image objects to validate.
 * @returns true if all images are valid, false otherwise.
 */
function validateImages(images: ICarImageElement[] = []) {
  for (const img of images) {
    if (!isObject(img) || typeof img.source !== 'string') {
      logger.error('Invalid car image structure:', img);
      return false;
    }
  }
  return true;
}

export function thumbnail(vehicle: IVehicle) {
  const firstImage = vehicle?.carImages?.items?.[0];
  if (firstImage && firstImage.type === 'image') {
    return generateThumbnailUrl(firstImage.source);
  }
  return undefined;
}
